import type { TimeSlot } from "./TicketApi";

/**
 * FAS-1053 — slot timestamps arrive as ISO-8601 carrying the EVENT's UTC offset. A 14:00 slot in
 * Amsterdam has to read "14:00" for a buyer in London too, so we format in the offset the string
 * came with instead of the browser's: shift the instant by that offset and format as UTC.
 * Falls back to local time when the string carries no offset.
 */
function formatInOriginalOffset(iso: string, locale: string, options: Intl.DateTimeFormatOptions): string {
    const instant = new Date(iso);
    if (isNaN(instant.getTime())) {
        return '';
    }

    const match = /(?:Z|([+-])(\d{2}):?(\d{2}))$/.exec(iso.trim());
    if (match === null) {
        return new Intl.DateTimeFormat(locale, options).format(instant);
    }

    const offsetMinutes = match[1] === undefined
        ? 0
        : (match[1] === '-' ? -1 : 1) * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));

    return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' })
        .format(new Date(instant.getTime() + offsetMinutes * 60000));
}

/** `14:00 – 15:00` */
export function timeSlotTimeRange(slot: TimeSlot, locale: string): string {
    const time: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    return `${formatInOriginalOffset(slot.startsAt, locale, time)} – ${formatInOriginalOffset(slot.endsAt, locale, time)}`;
}

/** `za 4 jul` */
export function timeSlotDay(slot: TimeSlot, locale: string): string {
    return formatInOriginalOffset(slot.startsAt, locale, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** `za 4 jul · 14:00 – 15:00`, or just the time range when the day is already obvious. */
export function timeSlotLabel(slot: TimeSlot, locale: string, withDay: boolean = true): string {
    const range = timeSlotTimeRange(slot, locale);
    return withDay ? `${timeSlotDay(slot, locale)} · ${range}` : range;
}

/** Calendar day of the slot's start, in the event's own offset — used to group/label rows. */
export function timeSlotDayKey(slot: TimeSlot): string {
    return formatInOriginalOffset(slot.startsAt, 'en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** True when the product's slots do not all fall on the same calendar day. */
export function timeSlotsSpanMultipleDays(slots: TimeSlot[]): boolean {
    return new Set(slots.map(timeSlotDayKey)).size > 1;
}
